/**
 * Permission utility functions
 * Handles access control for cost data and other sensitive information
 */

import { User, UserRole } from '../types';

/**
 * Check if a user can view cost data
 * Default permissions:
 * - Investors: YES
 * - Stakeholders: YES
 * - HR: YES
 * - Admin: YES
 * - Developers: NO (unless explicitly granted)
 * - External Partners: NO (unless explicitly granted)
 */
export function canViewCost(user: User | null): boolean {
  if (!user) return false;
  
  // Check explicit permission first
  if (user.permissions?.viewCost !== undefined) {
    return user.permissions.viewCost;
  }
  
  // Default permissions based on role
  const rolesWithCostAccess: UserRole[] = ['investor', 'stakeholder', 'hr', 'admin'];
  return rolesWithCostAccess.includes(user.role);
}

/**
 * Check if a user can edit cost data
 * Default permissions:
 * - Investors: YES
 * - Stakeholders: YES
 * - Admin: YES
 * - Others: NO (unless explicitly granted)
 */
export function canEditCost(user: User | null): boolean {
  if (!user) return false;
  
  // Check explicit permission first
  if (user.permissions?.editCost !== undefined) {
    return user.permissions.editCost;
  }
  
  // Default permissions based on role
  const rolesWithEditAccess: UserRole[] = ['investor', 'stakeholder', 'admin'];
  return rolesWithEditAccess.includes(user.role);
}

/**
 * Check if a user can view reports
 */
export function canViewReports(user: User | null): boolean {
  if (!user) return false;
  
  // Check explicit permission first
  if (user.permissions?.viewReports !== undefined) {
    return user.permissions.viewReports;
  }
  
  // Default: all roles except external partners can view reports
  return user.role !== 'external_partner';
}

/**
 * Check if a user can manage other users
 */
export function canManageUsers(user: User | null): boolean {
  if (!user) return false;
  
  // Check explicit permission first
  if (user.permissions?.manageUsers !== undefined) {
    return user.permissions.manageUsers;
  }
  
  // Default: only admin and HR can manage users
  return user.role === 'admin' || user.role === 'hr';
}

/**
 * Get a description of user permissions for display
 */
export function getPermissionSummary(user: User): string {
  const permissions: string[] = [];
  
  if (canViewCost(user)) permissions.push('View Cost');
  if (canEditCost(user)) permissions.push('Edit Cost');
  if (canViewReports(user)) permissions.push('View Reports');
  if (canManageUsers(user)) permissions.push('Manage Users');
  
  return permissions.length > 0 ? permissions.join(', ') : 'Basic Access';
}
